import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * DownloadOverlay — full-screen "rendering" treatment while the
 * server-side Playwright export is in flight.
 *
 * Voice: editorial, premium, technical-craft. NOT a generic spinner.
 *
 * Three things happen in sequence:
 *   1. The cardSlot (the card itself, dimmed) is shown front-and-center
 *   2. A soft amber light sweeps left-to-right across the card, like a
 *      film scanner pass — repeats while exporting === true
 *   3. A mono uppercase status ticker below cycles through stages:
 *        rendering · 9:16 · 1080×1920
 *        embedding sRGB color
 *        ready
 *
 * Props:
 *   open: boolean — controls visibility (no AnimatePresence outside, we own it)
 *   children: ReactNode — the card preview to show centered (small clone)
 *   meta?: { width, height, aspect } — optional override for the status ticker
 */
export default function DownloadOverlay({ open, children, meta }) {
    const [stage, setStage] = useState(0);
    const aspect = meta?.aspect || "9:16";
    const width = meta?.width || 1080;
    const height = meta?.height || 1920;
    const stages = [
        `rendering · ${aspect} · ${width}×${height}`,
        "embedding srgb color",
        "almost there…",
    ];

    useEffect(() => {
        if (!open) { setStage(0); return; }
        // Advance the status ticker every 1.6s. Caps at the last stage —
        // when the actual download resolves, the parent closes the overlay.
        const t = setInterval(() => {
            setStage((s) => Math.min(s + 1, stages.length - 1));
        }, 1600);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="download-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    className="fixed inset-0 z-[120] flex flex-col items-center justify-center px-6"
                    style={{
                        background: "rgba(8, 9, 14, 0.78)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                    }}
                    data-testid="download-overlay"
                >
                    {/* Eyebrow */}
                    <motion.span
                        className="font-sans text-[10px] uppercase tracking-[0.5em] text-amber-200/80 mb-6"
                        initial={{ y: -10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                    >
                        ✦ rendering your card
                    </motion.span>

                    {/* Card slot with scanner sweep */}
                    <div className="relative w-full max-w-[260px] sm:max-w-[300px] aspect-[9/16] rounded-2xl overflow-hidden">
                        {/* Card preview (clone) */}
                        <div className="absolute inset-0">{children}</div>

                        {/* Subtle dim layer so the sweep reads cleanly */}
                        <div className="absolute inset-0 bg-black/25 pointer-events-none" />

                        {/* Scanner sweep — repeats while overlay is open.
                            Amber band traveling top-to-bottom (matches the
                            9:16 portrait orientation) with a soft trailing
                            glow. 1.8s per pass. */}
                        <motion.div
                            className="absolute inset-x-0 pointer-events-none"
                            style={{ height: "30%" }}
                            initial={{ top: "-30%" }}
                            animate={{ top: "100%" }}
                            transition={{
                                duration: 1.8,
                                ease: "linear",
                                repeat: Infinity,
                            }}
                        >
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(255,200,120,0) 0%, rgba(255,200,120,0.18) 50%, rgba(255,200,120,0) 100%)",
                                    boxShadow: "0 0 28px rgba(255,200,120,0.22)",
                                }}
                            />
                            <div
                                className="absolute left-0 right-0"
                                style={{
                                    top: "50%",
                                    height: "1.5px",
                                    background:
                                        "linear-gradient(90deg, transparent 0%, rgba(255,210,140,0.85) 50%, transparent 100%)",
                                    boxShadow: "0 0 12px rgba(255,210,140,0.7)",
                                }}
                            />
                        </motion.div>

                        {/* Top + bottom edge feather so sweep doesn't cut */}
                        <div
                            className="absolute inset-x-0 top-0 h-8 pointer-events-none"
                            style={{ background: "linear-gradient(180deg, rgba(8,9,14,0.55), transparent)" }}
                        />
                        <div
                            className="absolute inset-x-0 bottom-0 h-8 pointer-events-none"
                            style={{ background: "linear-gradient(0deg, rgba(8,9,14,0.55), transparent)" }}
                        />
                    </div>

                    {/* Status ticker */}
                    <div className="mt-8 h-5 relative w-full max-w-[300px]">
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={stage}
                                initial={{ y: 8, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -6, opacity: 0 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                className="absolute inset-0 text-center font-sans text-[11px] uppercase tracking-[0.34em] text-app-2 whitespace-nowrap overflow-hidden text-ellipsis"
                                data-testid="download-overlay-status"
                            >
                                {stages[stage]}
                            </motion.span>
                        </AnimatePresence>
                    </div>

                    {/* Progress bar — indeterminate, never reaches 100% by itself */}
                    <div className="mt-5 w-full max-w-[260px] h-[2px] rounded-full bg-white/8 overflow-hidden">
                        <motion.div
                            className="h-full rounded-full"
                            style={{
                                background: "linear-gradient(90deg, rgba(255,210,140,0.2), rgba(255,210,140,0.85), rgba(255,210,140,0.2))",
                            }}
                            initial={{ x: "-30%", width: "30%" }}
                            animate={{ x: "130%" }}
                            transition={{ duration: 1.4, ease: "linear", repeat: Infinity }}
                        />
                    </div>

                    {/* Footer micro-hint */}
                    <p className="mt-6 font-serif italic text-app-3 text-xs text-center max-w-[260px]">
                        we're rendering pixel-identical to your preview. just a moment.
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
